<script setup lang="ts">
/**
 * Generic "Discard changes?" confirmation dialog. Used by the
 * matter-context modal's inline knowledge editor (Cancel-while-dirty)
 * and the modal close handler (Close-while-dirty). Text and button labels
 * default to the discard-knowledge wording but can be overridden per use.
 */
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

withDefaults(
    defineProps<{
        visible: boolean;
        title?: string;
        body?: string;
        cancelLabel?: string;
        discardLabel?: string;
        /** Render the confirm button in a destructive (red) style. */
        destructive?: boolean;
        /** Disable both buttons and show a progress bar while an action runs. */
        busy?: boolean;
    }>(),
    {
        title: undefined,
        body: undefined,
        cancelLabel: undefined,
        discardLabel: undefined,
        destructive: false,
        busy: false,
    },
);

defineEmits<{
    discard: [];
    cancel: [];
}>();
</script>

<template>
    <div v-if="visible" class="discard-dialog-overlay" @click.self="!busy && $emit('cancel')">
        <div class="discard-dialog" role="dialog" aria-modal="true">
            <h3 class="discard-dialog-title">{{ title ?? t('matterContext.discardDialog.title') }}</h3>
            <p class="discard-dialog-body">{{ body ?? t('matterContext.discardDialog.body') }}</p>
            <div class="discard-dialog-actions">
                <button
                    type="button"
                    class="discard-dialog-cancel"
                    :disabled="busy"
                    @click="$emit('cancel')"
                >
                    {{ cancelLabel ?? t('common.cancel') }}
                </button>
                <button
                    type="button"
                    class="discard-dialog-discard"
                    :class="{ 'discard-dialog-discard--destructive': destructive }"
                    :disabled="busy"
                    @click="$emit('discard')"
                >
                    {{ discardLabel ?? t('matterContext.discardDialog.discard') }}
                </button>
            </div>
            <div v-if="busy" class="modal-progress-bar" :aria-label="t('matterContext.discardDialog.working')"></div>
        </div>
    </div>
</template>
